import React, { useState, useEffect, useMemo } from 'react';
import { theme } from 'antd';
import { Dayjs } from 'dayjs';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import ComplianceEstLayout from 'components/Layouts/ComplianceEstLayout';
import TextBox from 'components/TextBox';
import CustomTable from 'components/Tables/CustomTable';
import CustomSelect from 'components/CustomSelect';
import CustomIconButton from 'components/CustomIconButton';
import CustomDatePicker from 'components/CustomDatePicker';
import TextAreaBoxTasks from 'components/TextAreaBoxTasks';
import { ITask, ITasksResult, ITaskToShow } from 'store/ducks/Tasks/types';
import Helpers from 'utils/Helpers';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import { complianceEstOp } from 'store/ducks/ComplienceEst';
import { tasksOp } from 'store/ducks/Tasks';
import { globalOp } from 'store/ducks/Global';
import { RootState } from 'store/configureStore';
import { useUnsavedChanges } from 'utils/useUnsavedChanges';
import useDeleteWithDependencyCheck from 'utils/hooks/useDeleteWithDependencyCheck';
import ApiService from 'utils/ApiCalls';
import { stripHtmlTags } from 'utils/stripHtmlTags';
import * as Styled from './TasksDefinition.styled';

const { useToken } = theme;

const TasksDefinition = () => {
  const dispatch = useDispatch();
  const { token } = useToken();
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const {
    lang,
    drawTable,
    loading,
    tasks,
    tasksToShow,
    tasksToSave,
    saveClicked
  } = useSelector((state: RootState) => ({
    lang: state.global.lang,
    drawTable: state.complianceEst.drawTable,
    loading: state.tasks.loading,
    tasks: state.tasks.tasks,
    tasksToShow: state.tasks.tasksToShow,
    tasksToSave: state.tasks.tasksToSave,
    saveClicked: state.complianceEst.saveClicked
  }));

  const [ownersList, setOwnersList] = useState<string[]>([]);
  const [taskTypes, setTaskTypes] = useState<string[]>([]);
  const [statusList, setStatusList] = useState<string[]>([]);

  const [solSearch, setSolSearch] = useState('');
  const [solSearchOpen, setSolSearchOpen] = useState(false);
  const [taskSearch, setTaskSearch] = useState('');
  const [taskSearchOpen, setTaskSearchOpen] = useState(false);

  const { handleDelete: handleTaskDelete } = useDeleteWithDependencyCheck({
    tableName: 'tasks',
    softDeleteFn: (id) => ApiService.softDeleteRecord('tasks', id),
    onSuccess: (id) => {
      dispatch(tasksOp.deleteTask(id, 0));
    },
    entityLabel: 'task'
  });

  const hasUnsavedChanges = useMemo(() => {
    return Object.keys(tasksToSave).length > 0;
  }, [tasksToSave]);

  useUnsavedChanges({
    hasChanges: hasUnsavedChanges
  });

  useEffect(() => {
    globalOp.fetchOwnersList().then((response: string[]) => {
      setOwnersList(
        [...response].filter(Boolean).sort((a, b) => a.localeCompare(b))
      );
    });

    globalOp.fetchStatusList(lang).then((response: string[]) => {
      setStatusList(
        [...response].filter(Boolean).sort((a, b) => a.localeCompare(b))
      );
    });

    globalOp.fetchTypesList(lang).then((response: string[]) => {
      setTaskTypes(
        [...response].filter(Boolean).sort((a, b) => a.localeCompare(b))
      );
    });
  }, []);

  useEffect(() => {
    if (!Helpers.isObjEmpty(tasksToSave) && saveClicked) {
      const tasksToSend = Object.values(tasksToSave)
        .filter((task) => task != null)
        .map((task) => ({
          ...task,
          tsk_id: String(task.tsk_id).startsWith('new-')
            ? undefined
            : task.tsk_id
        }));
      dispatch(tasksOp.saveTasks(tasksToSend, lang));
    }
  }, [saveClicked]);

  useEffect(() => {
    dispatch(complianceEstOp.setSaveDisabled(Helpers.isObjEmpty(tasksToSave)));
  }, [tasksToSave]);

  useEffect(() => {
    if (!tasks?.length) {
      dispatch(tasksOp.setTasksToShow([]));
      return;
    }

    const rows: ITaskToShow[] = [];

    tasks.forEach((item: ITasksResult) => {
      if (item.TASKS && Object.keys(item.TASKS).length) {
        Object.values(item.TASKS).forEach((task) => {
          rows.push({
            ...item,
            task_id: task.tsk_id,
            TASKS: {
              ...task,
              tsk_sol_ref: item.sol_id
            }
          });
        });
      } else {
        rows.push({
          ...item,
          task_id: `empty_${item.sol_id}`,
          TASKS: {
            tsk_id: Number(generateUniqueId()),
            tsk_short_name: '',
            tsk_desc: '',
            tsk_tkt_ref: '',
            tsk_tst_ref: '',
            tsk_owner: '',
            tsk_due_date: '',
            tsk_sol_ref: item.sol_id
          }
        });
      }
    });

    dispatch(tasksOp.setTasksToShow(rows));
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    if (solSearch.trim())
      return tasksToShow.filter((item) =>
        item.desc?.toLowerCase().includes(solSearch.toLowerCase())
      );
    if (taskSearch.trim())
      return tasksToShow.filter((item) =>
        item.TASKS?.tsk_desc?.toLowerCase().includes(taskSearch.toLowerCase())
      );
    return tasksToShow;
  }, [tasksToShow, solSearch, taskSearch]);

  const paginatedTasks = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredTasks.slice(start, start + pageSize);
  }, [filteredTasks, currentPage, pageSize]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredTasks.length / pageSize));
    if (currentPage > maxPage) setCurrentPage(1);
  }, [filteredTasks, pageSize]);

  const isFirstSolutionRow = (index: number, row: ITaskToShow) => {
    if (index === 0) return true;
    return paginatedTasks[index - 1]?.sol_id !== row.sol_id;
  };

  const solutionRowSpan = (row: ITaskToShow) => {
    return paginatedTasks.filter((item) => item.sol_id === row.sol_id).length;
  };

  const addTask = (solId: number) => {
    dispatch(tasksOp.addNewTask(solId));
  };

  const columns = [
    {
      title: () => (
        <Styled.ColumnHeaderContainer>
          <span>{t('SOLUTION')}</span>
          <Styled.SearchIcon onClick={() => setSolSearchOpen(!solSearchOpen)} />
          {solSearchOpen && (
            <Styled.StyledSearchInput
              placeholder='Search'
              value={solSearch}
              onChange={(e) => setSolSearch(e.target.value)}
              autoFocus
            />
          )}
        </Styled.ColumnHeaderContainer>
      ),
      dataIndex: 'desc',
      key: 'desc',
      width: solSearchOpen ? 700 : 500,
      render: (desc: string, row: ITaskToShow, index: number) => ({
        children: (
          <div>
            <TextBox width={solSearchOpen ? 700 : 500}>
              {stripHtmlTags(desc)}
            </TextBox>
          </div>
        ),
        props: {
          rowSpan: isFirstSolutionRow(index, row) ? solutionRowSpan(row) : 0
        }
      })
    },

    {
      title: () => (
        <Styled.ColumnHeaderContainer>
          <span>{t('TASK_DEFINITION')}</span>
          <Styled.SearchIcon
            onClick={() => setTaskSearchOpen(!taskSearchOpen)}
          />
          {taskSearchOpen && (
            <Styled.StyledSearchInput
              placeholder='Search'
              value={taskSearch}
              onChange={(e) => setTaskSearch(e.target.value)}
              autoFocus
            />
          )}
        </Styled.ColumnHeaderContainer>
      ),
      dataIndex: 'TASKS',
      key: 'TASKS',
      width: 366,

      render: (TASKS: ITask, row: ITaskToShow, index: number) => {
        const lastRow =
          index === paginatedTasks.length - 1 ||
          paginatedTasks[index + 1]?.sol_id !== row.sol_id;

        return (
          <Styled.TextAreaDiv>
            <TextAreaBoxTasks
              key={String(row.task_id)}
              task={TASKS}
              solId={row.sol_id}
              taskId={row.task_id}
            />

            {lastRow && (
              <Styled.ButtonContainer>
                <CustomIconButton
                  type='primary'
                  shape='circle'
                  icon={<PlusOutlined />}
                  size='middle'
                  onClick={() => addTask(row.sol_id)}
                />
              </Styled.ButtonContainer>
            )}
          </Styled.TextAreaDiv>
        );
      }
    },
    {
      title: t('TASK_TYPE'),
      dataIndex: 'TASKS',
      key: 'TASKS',
      width: 100,

      render: (TASKS: ITask, row: ITaskToShow) =>
        TASKS ? (
          <CustomSelect
            options={Helpers.convertToSelectOptions(taskTypes)}
            defaultValue={TASKS.tsk_tkt_ref}
            onChange={(value: string) => {
              dispatch(
                tasksOp.editTaskValue(row.task_id, TASKS, 'tsk_tkt_ref', value)
              );

              dispatch(complianceEstOp.setSaveDisabled(false));
            }}
          />
        ) : (
          <Styled.StyledTextBox width={100} />
        )
    },

    {
      title: t('TASK_STATUS'),
      dataIndex: 'TASKS',
      key: 'TASKS',
      width: 120,

      render: (TASKS: ITask, row: ITaskToShow) =>
        TASKS ? (
          <CustomSelect
            size='md'
            options={Helpers.convertToSelectOptions(statusList)}
            defaultValue={TASKS.tsk_tst_ref}
            onChange={(value: string) => {
              dispatch(
                tasksOp.editTaskValue(row.task_id, TASKS, 'tsk_tst_ref', value)
              );

              dispatch(complianceEstOp.setSaveDisabled(false));
            }}
          />
        ) : (
          <Styled.StyledTextBox width={120} />
        )
    },

    {
      title: t('OWNER'),
      dataIndex: 'TASKS',
      key: 'TASKS',
      width: 150,

      render: (TASKS: ITask, row: ITaskToShow) =>
        TASKS ? (
          <CustomSelect
            size='lg'
            options={Helpers.convertToSelectOptions(ownersList)}
            defaultValue={TASKS.tsk_owner}
            onChange={(value: string) => {
              dispatch(
                tasksOp.editTaskValue(row.task_id, TASKS, 'tsk_owner', value)
              );

              dispatch(complianceEstOp.setSaveDisabled(false));
            }}
          />
        ) : (
          <Styled.StyledTextBox width={150} />
        )
    },

    {
      title: t('DUE_DATE'),
      dataIndex: 'TASKS',
      key: 'TASKS',
      width: 128,

      render: (TASKS: ITask, row: ITaskToShow) =>
        TASKS ? (
          <CustomDatePicker
            defaultValue={TASKS.tsk_due_date}
            onChange={(date: Dayjs, dateString: string) => {
              dispatch(
                tasksOp.editTaskValue(
                  row.task_id,
                  TASKS,
                  'tsk_due_date',
                  dateString
                )
              );

              dispatch(complianceEstOp.setSaveDisabled(false));
            }}
          />
        ) : (
          <Styled.StyledTextBox width={128} />
        )
    }
  ];

  return (
    <ComplianceEstLayout>
      <Styled.ContentWrapper token={token}>
        {drawTable && tasksToShow.length ? (
          <Styled.StyledCustomTable
            rowKey={(record: ITaskToShow) => String(record.task_id)}
            drawTable
            token={token}
            columns={columns}
            dataSource={paginatedTasks}
            itemsCount={filteredTasks.length}
            loading={loading}
            pagination={{
              current: currentPage,
              pageSize,
              total: filteredTasks.length,
              showSizeChanger: true,
              onChange: (
                page: React.SetStateAction<number>,
                size: React.SetStateAction<number>
              ) => {
                setCurrentPage(page);
                if (size !== pageSize) {
                  setPageSize(size);
                  setCurrentPage(1);
                }
              }
            }}
          />
        ) : (
          <CustomTable
            drawTable
            itemsCount={0}
            columns={columns}
            dataSource={[]}
          />
        )}
      </Styled.ContentWrapper>
    </ComplianceEstLayout>
  );
};

export default TasksDefinition;
